import React from "react";
import { generatePath, useParams } from "react-router";
import { Button, DataTable, Link, Radio, Table } from "@sscs/ui";
import { ActionMenu, AllowedTo } from "Components";
import { FULL_SINGLE_GROUP_PATH, PERMISSION } from "Constants";
import { useAllowedTo, useDialog, useLoginInfo } from "Hooks";
import { ActionItem, Group } from "Types";
import useShowAddMemberColumn from "../../hooks/useShowAddMemberColumn";
import { useUpdateGroupMutation } from "../../services/organizations.api";
import AddExistingMember from "../AddExistingMember/AddExistingMember";
import EditGroup from "../EditGroup/EditGroup";
import GroupMembers from "../GroupMembers/GroupMembers";
import GroupName from "../GroupName/GroupName";
import RemoveGroup from "../RemoveGroup/RemoveGroup";

type GroupRowProps = {
  group: Group;
  expanded: boolean;
  totalPortalCapacity?: number;
  onToggle: (willExpand: boolean) => void;
};

const GroupRow = ({
  group,
  expanded,
  totalPortalCapacity,
  onToggle,
}: GroupRowProps): React.JSX.Element => {
  const { organizationId, membersGroupId: urlGroupId } = useParams();
  const { isSSOLogin, hasLoaded } = useLoginInfo();
  const showAddMemberColumn = useShowAddMemberColumn();
  const canViewMembers = useAllowedTo({
    permissions: [PERMISSION.VIEW_GROUP_MEMBERS],
    groupId: group.group_id,
  });

  const editDialog = useDialog();
  const removeDialog = useDialog();
  const addMemberDialog = useDialog();
  const [updateGroup] = useUpdateGroupMutation();

  const groupDetailsPath = `/${generatePath(FULL_SINGLE_GROUP_PATH, {
    organizationId: organizationId ?? "",
    membersGroupId: group.group_id,
  })}`;

  const actions: ActionItem[] = [
    {
      text: "Edit group",
      type: "EDIT",
      onClick: editDialog.open,
      permissions: [PERMISSION.CREATE_GROUP],
    },
    {
      text: "Delete group",
      type: "DELETE",
      onClick: removeDialog.open,
      permissions: [PERMISSION.DELETE_GROUP],
    },
  ];

  return (
    <>
      {editDialog.isOpen && (
        <EditGroup
          isOpen={editDialog.isOpen}
          group={group}
          onClose={editDialog.close}
          showSSOGroupMapping={isSSOLogin && hasLoaded}
          totalPortalCapacity={totalPortalCapacity}
        />
      )}
      <RemoveGroup
        isOpen={removeDialog.isOpen}
        group={group}
        onClose={removeDialog.close}
      />
      {addMemberDialog.isOpen && (
        <AddExistingMember
          groupId={group.group_id}
          isOpen={addMemberDialog.isOpen}
          onClose={addMemberDialog.close}
        />
      )}
      <DataTable.ExpandedRow
        expanded={canViewMembers && expanded}
        disabled={!canViewMembers}
        focused={urlGroupId === group.group_id}
        onChange={onToggle}
      >
        <DataTable.ExpandedRow.Summary>
          <Table.Cell>
            {canViewMembers ? (
              <Link
                to={groupDetailsPath}
                title={group.name}
                onClick={(event) => {
                  if (
                    event.metaKey ||
                    event.ctrlKey ||
                    event.shiftKey ||
                    event.altKey
                  ) {
                    return;
                  }

                  event.preventDefault();
                  onToggle(!expanded);
                }}
              >
                <GroupName name={group.name} isDefault={group.is_default} />
              </Link>
            ) : (
              <GroupName name={group.name} isDefault={group.is_default} />
            )}
          </Table.Cell>
          <Table.Cell align="center">{group.member_count}</Table.Cell>
          <AllowedTo permissions={[PERMISSION.SET_GROUP_AS_DEFAULT]}>
            <Table.Cell align="center">
              <Radio
                checked={group.is_default}
                onChange={(event) => {
                  if (event.target.checked) {
                    // no need for notification because change
                    // is visible on the UI immediately
                    updateGroup({
                      groupId: group.group_id,
                      body: {
                        is_default: true,
                      },
                    });
                  }
                }}
              />
            </Table.Cell>
          </AllowedTo>
          {showAddMemberColumn && (
            <Table.Cell align="center">
              <AllowedTo
                permissions={[PERMISSION.ADD_ORGANIZATION_MEMBERS_TO_GROUP]}
                groupId={group.group_id}
              >
                <Button
                  color="secondary"
                  size="small"
                  onClick={addMemberDialog.open}
                >
                  Add member
                </Button>
              </AllowedTo>
            </Table.Cell>
          )}
          <AllowedTo
            permissions={[PERMISSION.CREATE_GROUP, PERMISSION.DELETE_GROUP]}
          >
            <Table.Cell align="center">
              <ActionMenu items={actions} />
            </Table.Cell>
          </AllowedTo>
        </DataTable.ExpandedRow.Summary>
        <DataTable.ExpandedRow.Details>
          <GroupMembers groupId={group.group_id} />
        </DataTable.ExpandedRow.Details>
      </DataTable.ExpandedRow>
    </>
  );
};

export default GroupRow;
